import { Debug } from '@/components/Models/debug'
import type { useSpotDetailAttachedStateMachine } from '@/features/pages/spots/detail/useSpotDetailAttachedStateMachine'
import {
  GPS_ACCURACY_THRESHOLD,
  type useStampFlowStateMachine,
} from '@/features/pages/spots/detail/useStampFlowStateMachine'

type StampState = ReturnType<typeof useStampFlowStateMachine>
type AttachedState = ReturnType<typeof useSpotDetailAttachedStateMachine>

type Props = {
  stamp: StampState
  attached: AttachedState
}

const resolveStampStateName = (stamp: StampState): string => {
  if (stamp.isLoading) return 'initialLoading'
  if (stamp.isError) return 'error'
  if (stamp.isGpsRefreshing) return 'gpsRefreshing'
  if (stamp.isPopupShown) return 'popupShown'
  if (stamp.isDescriptiveQuizInput) return 'descriptiveQuizInput'
  if (stamp.isQuizAnswering) return 'quizAnswering'
  if (stamp.isQuizChoice) return 'quizChoice'
  if (stamp.isQuizCorrect) return 'quizCorrect'
  if (stamp.isQuizIncorrect) return 'quizIncorrect'
  if (stamp.isQrScan) return 'qrScan'
  if (stamp.isBarcodeScan) return 'barcodeScan'
  if (stamp.isOcrWarning) return 'ocrWarning'
  if (stamp.isOcrCapture) return 'ocrCapture'
  if (stamp.isOcrReadFailure) return 'ocrReadFailure'
  if (stamp.isAcquiring) return 'acquiring'
  if (stamp.isAcquireError) return 'acquireError'
  if (stamp.isGpsRangeError) return 'gpsRangeError'
  if (stamp.isStampShowing) return 'stampShowing'
  if (stamp.isRewardShowing) return 'rewardShowing'
  if (stamp.isPhotoPost) return 'photoPost'
  if (stamp.isRedisplayPopupShown) return 'redisplayPopupShown'
  if (stamp.isRedisplayStampShowing) return 'redisplayStampShowing'
  if (stamp.isIdle) return 'idle'
  return 'unknown'
}

const resolveAttachedStateName = (attached: AttachedState): string => {
  if (attached.isArPhotoLocked) return 'arPhotoLocked'
  if (attached.isAudioGuide) return 'audioGuide'
  if (attached.isAudioGuideLocked) return 'audioGuideLocked'
  return 'idle'
}

export const SpotDetailStateMachineDebug = ({ stamp, attached }: Props) => {
  const { context: ctx } = stamp
  const gpsAccuracy = ctx.gps.loaded ? `${Math.round(ctx.gps.accuracy)}m` : '未取得'
  const gpsStatus = !ctx.gps.loaded ? 'loading' : ctx.gps.accuracy > GPS_ACCURACY_THRESHOLD ? 'invalid' : 'ok'

  return (
    <>
      {process.env.NODE_ENV !== 'production' && (
        <>
          <Debug
            title="stamp-flow"
            text={[
              `state := ${resolveStampStateName(stamp)}`,
              `spotId := ${ctx.spotId}`,
              `isAuthenticated := ${ctx.isAuthenticated}`,
              `isAuthInitialized := ${ctx.isAuthInitialized}`,
              `activeStampId := ${ctx.activeStampId ?? 'null'}`,
              `activeStampType := ${ctx.activeStampType ?? 'null'}`,
              `hasAnyStamp := ${ctx.hasAnyStamp} / hasAllStamps := ${ctx.hasAllStamps}`,
              `acquiredStampIds := [${ctx.acquiredStampIds.join(', ')}]`,
              `redisplayStampIds := [${ctx.redisplayStampIds.join(', ')}]`,
              `stampIds := [${ctx.stampIds.join(', ')}]`,
              `spot.stamps.length := ${ctx.spot?.stamps.length ?? 'null'}`,
              `gps.accuracy := ${gpsAccuracy} [${gpsStatus}]`,
              `gps.loaded := ${ctx.gps.loaded}`,
              `acquiredStamps.length := ${ctx.acquiredStamps.length}`,
              `currentStampIndex := ${ctx.currentStampIndex}`,
              `acquiredRewards.length := ${ctx.acquiredRewards.length}`,
              `pendingTextAnswer := ${ctx.pendingTextAnswer ?? 'null'}`,
              `errorMessage := ${ctx.errorMessage ?? 'null'}`,
            ]}
          />
          <Debug title="spot-detail-attached" text={[`state := ${resolveAttachedStateName(attached)}`]} />
        </>
      )}
    </>
  )
}
